<script setup>
import StatusChip from '@/components/shared/StatusChip.vue'
import { railsApi } from '@/utils/railsApi'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t } = useI18n()

const loading = ref(false)
const saving = ref(false)
const clearing = ref(false)
const testing = ref(false)
const error = ref('')
const success = ref('')
const testFeedback = ref(null)
const connection = ref(null)
const apiKeyInput = ref('')
const savePassword = ref('')
const clearPassword = ref('')
const clearAcknowledged = ref(false)

const saveDialog = ref(false)
const clearDialog = ref(false)

const formatWhen = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleString('nl-NL')
}

const configuredLabel = computed(() => {
  if (!connection.value)
    return ''
  if (!connection.value.configured)
    return t('admin.mollieConnection.status.notConfigured')
  if (connection.value.mode === 'live')
    return t('admin.mollieConnection.status.configuredLive')
  if (connection.value.mode === 'test')
    return t('admin.mollieConnection.status.configuredTest')

  return t('admin.mollieConnection.status.configuredUnknownMode')
})

const sourceState = computed(() => connection.value?.source || 'none')

const canClear = computed(() => !!connection.value?.admin_key_present)
const canSave = computed(() => apiKeyInput.value.trim().length > 0)
const isRotating = computed(() => !!connection.value?.admin_key_present)

const canConfirmSave = computed(() => {
  return canSave.value
    && !saving.value
    && (!isRotating.value || !!savePassword.value)
})

const canConfirmClear = computed(() => {
  return !clearing.value
    && clearAcknowledged.value
    && clearPassword.value.length > 0
})

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const data = await railsApi('/api/v1/admin/mollie_connection')

    connection.value = data.mollie_connection
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.mollieConnection.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const openSaveDialog = () => {
  if (!canSave.value)
    return
  error.value = ''
  success.value = ''
  testFeedback.value = null
  savePassword.value = ''
  saveDialog.value = true
}

const openClearDialog = () => {
  error.value = ''
  success.value = ''
  testFeedback.value = null
  clearPassword.value = ''
  clearAcknowledged.value = false
  clearDialog.value = true
}

const confirmSave = async () => {
  if (!canConfirmSave.value)
    return
  saving.value = true
  error.value = ''
  success.value = ''
  testFeedback.value = null
  try {
    const body = {
      mollie_connection: { api_key: apiKeyInput.value.trim() },
    }

    if (isRotating.value)
      body.current_password = savePassword.value

    const data = await railsApi('/api/v1/admin/mollie_connection', {
      method: 'PATCH',
      body,
    })

    connection.value = data.mollie_connection
    apiKeyInput.value = ''
    savePassword.value = ''
    saveDialog.value = false
    success.value = t('admin.mollieConnection.saveSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.mollieConnection.saveError')
  }
  finally {
    saving.value = false
  }
}

const confirmClear = async () => {
  if (!canConfirmClear.value)
    return
  clearing.value = true
  error.value = ''
  success.value = ''
  testFeedback.value = null
  try {
    const data = await railsApi('/api/v1/admin/mollie_connection', {
      method: 'DELETE',
      body: { current_password: clearPassword.value },
    })

    connection.value = data.mollie_connection
    clearPassword.value = ''
    clearAcknowledged.value = false
    clearDialog.value = false
    success.value = t('admin.mollieConnection.clearSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.mollieConnection.clearError')
  }
  finally {
    clearing.value = false
  }
}

const testConnection = async () => {
  testing.value = true
  error.value = ''
  success.value = ''
  testFeedback.value = null
  try {
    const data = await railsApi('/api/v1/admin/mollie_connection/test', {
      method: 'POST',
    })

    connection.value = data.mollie_connection
    testFeedback.value = data.test || {
      success: false,
      message: t('admin.mollieConnection.testError'),
    }
  }
  catch (err) {
    testFeedback.value = {
      success: false,
      message: err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.mollieConnection.testError'),
    }
  }
  finally {
    testing.value = false
  }
}

const copyWebhook = async () => {
  if (!connection.value?.webhook_url || !navigator.clipboard)
    return
  try {
    await navigator.clipboard.writeText(connection.value.webhook_url)
    success.value = t('admin.mollieConnection.webhookCopied')
  }
  catch {
    error.value = t('admin.mollieConnection.webhookCopyError')
  }
}

defineExpose({
  canConfirmSave,
  canConfirmClear,
  isRotating,
  openSaveDialog,
  openClearDialog,
})
</script>

<template>
  <div>
    <h1 class="text-h4 mb-2">
      {{ $t('admin.mollieConnection.title') }}
    </h1>
    <p class="text-medium-emphasis mb-4">
      {{ $t('admin.mollieConnection.subtitle') }}
    </p>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>
    <VAlert
      v-if="success"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ success }}
    </VAlert>

    <div
      v-if="loading"
      role="status"
    >
      {{ $t('common.loading') }}
    </div>

    <template v-else-if="connection">
      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardTitle>{{ $t('admin.mollieConnection.statusTitle') }}</VCardTitle>
        <VCardText>
          <div class="d-flex flex-wrap align-center ga-2 mb-3">
            <span class="text-body-1">{{ configuredLabel }}</span>
            <StatusChip
              domain="mollie_connection_source"
              :state="sourceState"
            />
            <StatusChip
              v-if="connection.mode"
              domain="mollie_mode"
              :state="connection.mode"
            />
          </div>

          <p class="mb-1">
            <strong>{{ $t('admin.mollieConnection.adminKey') }}:</strong>
            {{ connection.admin_key_present
              ? $t('admin.mollieConnection.present')
              : $t('admin.mollieConnection.absent') }}
          </p>
          <p class="mb-3">
            <strong>{{ $t('admin.mollieConnection.hostingFallback') }}:</strong>
            {{ connection.hosting_fallback_present
              ? $t('admin.mollieConnection.present')
              : $t('admin.mollieConnection.absent') }}
          </p>

          <div
            class="mb-0"
            role="status"
            aria-live="polite"
          >
            <p class="mb-1">
              <strong>{{ $t('admin.mollieConnection.lastTest') }}:</strong>
              <template v-if="connection.last_test_outcome">
                {{ formatWhen(connection.last_tested_at) }}
              </template>
              <template v-else>
                {{ $t('admin.mollieConnection.noTestYet') }}
              </template>
            </p>
            <div
              v-if="connection.last_test_outcome"
              class="d-flex flex-wrap align-center ga-2"
            >
              <StatusChip
                domain="mollie_test_outcome"
                :state="connection.last_test_outcome"
              />
              <span class="text-body-2">{{ connection.last_test_message }}</span>
            </div>
          </div>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardTitle>{{ $t('admin.mollieConnection.webhookTitle') }}</VCardTitle>
        <VCardText>
          <p class="text-body-2 text-medium-emphasis mb-3">
            {{ $t('admin.mollieConnection.webhookHelp') }}
          </p>
          <div class="d-flex flex-wrap align-center ga-2">
            <code class="text-body-2">{{ connection.webhook_url }}</code>
            <VBtn
              type="button"
              size="small"
              variant="tonal"
              :aria-label="$t('admin.mollieConnection.copyWebhookAria')"
              @click="copyWebhook"
            >
              {{ $t('admin.mollieConnection.copyWebhook') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
        class="mb-4"
      >
        <VCardTitle>{{ $t('admin.mollieConnection.keyTitle') }}</VCardTitle>
        <VCardText>
          <VAlert
            type="info"
            variant="tonal"
            class="mb-4"
            role="status"
          >
            {{ $t('admin.mollieConnection.writeOnlyHelp') }}
          </VAlert>

          <VTextField
            v-model="apiKeyInput"
            type="password"
            autocomplete="new-password"
            :label="$t('admin.mollieConnection.apiKeyLabel')"
            :hint="$t('admin.mollieConnection.apiKeyHint')"
            persistent-hint
            class="mb-4"
            hide-details="auto"
          />

          <div class="d-flex flex-wrap ga-2">
            <VBtn
              color="primary"
              :disabled="!canSave || saving || clearing"
              @click="openSaveDialog"
            >
              {{ connection.admin_key_present
                ? $t('admin.mollieConnection.rotateKey')
                : $t('admin.mollieConnection.saveKey') }}
            </VBtn>
            <VBtn
              color="error"
              variant="tonal"
              :disabled="!canClear || saving || clearing"
              @click="openClearDialog"
            >
              {{ $t('admin.mollieConnection.clearKey') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <VCard
        flat
        border
      >
        <VCardTitle>{{ $t('admin.mollieConnection.testTitle') }}</VCardTitle>
        <VCardText>
          <p class="text-body-2 text-medium-emphasis mb-4">
            {{ $t('admin.mollieConnection.testHelp') }}
          </p>
          <VBtn
            color="primary"
            variant="tonal"
            :loading="testing"
            :disabled="saving || clearing"
            @click="testConnection"
          >
            {{ $t('admin.mollieConnection.testConnection') }}
          </VBtn>

          <VAlert
            v-if="testFeedback"
            class="mt-4"
            :type="testFeedback.success ? 'success' : 'error'"
            variant="tonal"
            :role="testFeedback.success ? 'status' : 'alert'"
          >
            {{ testFeedback.message }}
          </VAlert>
        </VCardText>
      </VCard>
    </template>

    <VDialog
      v-model="saveDialog"
      max-width="520"
      aria-modal="true"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>
            {{ isRotating
              ? $t('admin.mollieConnection.rotateConfirmTitle')
              : $t('admin.mollieConnection.saveConfirmTitle') }}
          </VCardTitle>
        </VCardItem>
        <VCardText>
          <p class="mb-4">
            {{ isRotating
              ? $t('admin.mollieConnection.rotateConfirmBody')
              : $t('admin.mollieConnection.saveConfirmBody') }}
          </p>
          <VTextField
            v-if="isRotating"
            v-model="savePassword"
            type="password"
            autocomplete="current-password"
            :label="$t('admin.mollieConnection.currentPasswordLabel')"
            :hint="$t('admin.mollieConnection.currentPasswordHint')"
            persistent-hint
            hide-details="auto"
            class="mb-2"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="saving"
            @click="saveDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            :disabled="!canConfirmSave"
            @click="confirmSave"
          >
            {{ isRotating
              ? $t('admin.mollieConnection.rotateKey')
              : $t('admin.mollieConnection.saveKey') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="clearDialog"
      max-width="520"
      aria-modal="true"
    >
      <VCard flat>
        <VCardItem>
          <VCardTitle>
            {{ $t('admin.mollieConnection.clearConfirmTitle') }}
          </VCardTitle>
        </VCardItem>
        <VCardText>
          <p class="mb-4">
            {{ $t('admin.mollieConnection.clearConfirmBody') }}
          </p>
          <VTextField
            v-model="clearPassword"
            type="password"
            autocomplete="current-password"
            :label="$t('admin.mollieConnection.currentPasswordLabel')"
            :hint="$t('admin.mollieConnection.currentPasswordHint')"
            persistent-hint
            hide-details="auto"
            class="mb-4"
          />
          <VCheckbox
            v-model="clearAcknowledged"
            :label="$t('admin.mollieConnection.clearAcknowledge')"
            :disabled="clearing"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="clearing"
            @click="clearDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="error"
            :loading="clearing"
            :disabled="!canConfirmClear"
            @click="confirmClear"
          >
            {{ $t('admin.mollieConnection.clearKey') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
